import type { TFunction } from "i18next";
import type { BrandBrainSectionId } from "@nexus/contracts";

const SECTION_LABEL_DEFAULTS: Record<BrandBrainSectionId, string> = {
  identity: "Identity",
  "brand-profile": "Brand profile",
  "audience-growth": "Audience & growth",
  products: "Products",
  "messaging-guidelines": "Messaging & guidelines",
  "commercial-context": "Commercial context",
  "competitive-context": "Competitive context",
  "enriched-brand-knowledge": "Enriched brand knowledge",
  "measurement-context": "Measurement context",
  "production-kit": "Production kit",
};

const FIELD_LABEL_DEFAULTS: Record<string, string> = {
  website_url: "Website",
  premise: "Brand premise",
  differentiation: "Differentiation",
  challenges: "Challenges",
  target_audience: "Target audience",
  growth_target: "Growth targets",
  product_to_focus_on: "Focus products",
  product_variance: "Categories & variants",
  best_sellers: "Best sellers",
  bundeling: "Bundles",
  product_description: "Product description",
  product_benefits: "Product benefits",
  tone_of_voice: "Tone of voice",
  messaging_do: "Messaging do's",
  messaging_dont: "Messaging don'ts",
  approved_claims: "Approved claims",
  what_works: "What works",
  things_to_avoid: "Things to avoid",
  content_strategy: "Content strategy",
  product_pricing_and_discounts: "Pricing & discounts",
  promotions: "Active promotions",
  engagement: "Engagement / retention",
  logistic_support: "Logistics & support",
  known_competitors: "Known competitors",
  nemesis: "Nemesis",
  who_to_ignore: "Who to ignore",
  inspiration_industry: "Inspiration / industry",
  visual_production_rules: "Visual & production rules",
  industry_category: "Category",
  primary_geography: "Geography",
  product_categories: "Product mix",
  revenue_model: "Revenue model",
  hero_product: "Hero product",
  growth_trend: "Growth trend",
};

export function brandBrainSectionLabel(t: TFunction, id: BrandBrainSectionId): string {
  return t(`brandBrain.sections.${id}`, SECTION_LABEL_DEFAULTS[id]);
}

/** One short line under the label in edit mode, for the fields Brand Brain owns. */
const FIELD_HINT_DEFAULTS: Record<string, string> = {
  approved_claims:
    "Exact approved claims, per product and for the brand. Add claims to hold back, disclaimer wording and before/after rules. Saved exactly as written.",
  what_works: "Concepts, hook families and angles that have worked, with examples.",
  visual_production_rules:
    "Aspect ratios, visual style, captions, how to show the product, b-roll, kit items, end cards and allowed longer formats.",
};

export function brandBrainFieldLabel(t: TFunction, column: string): string {
  return t(`brandBrain.fields.${column}`, FIELD_LABEL_DEFAULTS[column] ?? column);
}

export function brandBrainFieldHint(t: TFunction, column: string): string | undefined {
  const hint = FIELD_HINT_DEFAULTS[column];
  return hint ? t(`brandBrain.hints.${column}`, hint) : undefined;
}
